import {
  campaignInputSchema,
  MAX_CAMPAIGN_KEYWORDS,
  normaliseKeyword,
  slugifyCampaign,
} from "@purlink/shared/campaigns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, Link2 } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "~/components/shell/app-shell";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Combobox, TagInput } from "~/components/ui/combobox";
import { Alert } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toast } from "~/components/ui/toaster";
import {
  campaignActions,
  campaignKeys,
  campaignSuggestionsQuery,
} from "~/features/campaigns/api";
import { buildCampaignUrl, enquiryFormUrl } from "~/features/campaigns/link";
import { errorMessage } from "~/lib/auth";
import { useCan } from "~/lib/use-can";

export const Route = createFileRoute("/_app/settings/lead-form")({
  component: LeadFormSettings,
});

const presets = {
  google: { label: "Google Ads", source: "google", medium: "cpc" },
  microsoft: { label: "Microsoft Ads (Bing)", source: "bing", medium: "cpc" },
  meta: { label: "Facebook & Instagram ads", source: "facebook", medium: "paid_social" },
  linkedin: { label: "LinkedIn ads", source: "linkedin", medium: "paid_social" },
  tiktok: { label: "TikTok ads", source: "tiktok", medium: "paid_social" },
  email: { label: "Email newsletter", source: "newsletter", medium: "email" },
  custom: { label: "Something else", source: "", medium: "" },
} as const;
type Preset = keyof typeof presets;

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error("Couldn't copy. Select the link and copy it manually.");
        }
      }}
    >
      {copied ? <Check /> : <Copy />} {copied ? "Copied" : label}
    </Button>
  );
}

function LeadFormSettings() {
  const formUrl = enquiryFormUrl();
  const [preset, setPreset] = useState<Preset>("google");
  const [source, setSource] = useState<string>(presets.google.source);
  const [medium, setMedium] = useState<string>(presets.google.medium);
  const [campaign, setCampaign] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [content, setContent] = useState("");
  /** The link for the campaign as last saved; cleared whenever a field changes. */
  const [saved, setSaved] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canSave = useCan()("campaign", "create");
  const suggestions = useQuery(campaignSuggestionsQuery);

  const edited =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setSaved(null);
    };

  const input = { source, medium, name: campaign, keywords };
  const parsed = campaignInputSchema.safeParse(input);
  const ready = parsed.success;
  const filledIn = [parsed.data?.source, parsed.data?.medium, parsed.data?.name].filter(
    Boolean,
  ).length;
  const preview = useMemo(
    () => buildCampaignUrl(formUrl, { source, medium, campaign, keywords, content }),
    [formUrl, source, medium, campaign, keywords, content],
  );

  const save = useMutation({
    mutationFn: () => campaignActions.save(input),
    onSuccess: async ({ id, created }) => {
      const url = buildCampaignUrl(formUrl, { source, medium, campaign, keywords, content, id });
      setSaved(url);
      await queryClient.invalidateQueries({ queryKey: campaignKeys.all });
      try {
        await navigator.clipboard.writeText(url);
        toast.success(created ? "Campaign saved and link copied." : "Link copied.", {
          action: { label: "View campaigns", onClick: () => void navigate({ to: "/utm-campaigns" }) },
        });
      } catch {
        toast.success("Campaign saved. Select the link below and copy it.");
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader
        title="Lead form"
        description="Share your enquiry form and create tagged links so you can see which campaigns bring in leads."
      />
      <div className="flex max-w-3xl flex-col gap-6">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Your enquiry form</CardTitle>
              <CardDescription>Anyone with this link can send you an enquiry.</CardDescription>
            </div>
          </CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              readOnly
              value={formUrl}
              aria-label="Enquiry form link"
              className="font-mono text-small"
            />
            <div className="flex gap-2">
              <CopyButton value={formUrl} label="Copy" />
              <Button variant="secondary" asChild>
                <a href={formUrl} target="_blank" rel="noreferrer">
                  <ExternalLink /> Open
                </a>
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Campaign link builder</CardTitle>
              <CardDescription>
                Use a tagged link in each ad. Purlink records the tags with every lead, so the
                dashboard can show which campaigns work.
              </CardDescription>
            </div>
          </CardHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Where will the link appear?" className="sm:col-span-2">
              <Select
                value={preset}
                onValueChange={(value) => {
                  const next = value as Preset;
                  setPreset(next);
                  setSource(presets[next].source);
                  setMedium(presets[next].medium);
                  setSaved(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(presets).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Source" description="The platform, e.g. google">
              <Combobox
                value={source}
                onChange={edited(setSource)}
                options={suggestions.data?.sources ?? []}
              />
            </Field>
            <Field label="Medium" description="The type of traffic, e.g. cpc or email">
              <Combobox
                value={medium}
                onChange={edited(setMedium)}
                options={suggestions.data?.mediums ?? []}
              />
            </Field>
            <Field
              label="Campaign name"
              description={
                campaign ? `Saved as “${slugifyCampaign(campaign)}”` : "e.g. Spring SEO 2026"
              }
              className="sm:col-span-2"
            >
              <Combobox
                value={campaign}
                onChange={edited(setCampaign)}
                options={suggestions.data?.names ?? []}
              />
            </Field>
            <Field
              label="Keywords (optional)"
              description="Search keywords this ad targets. Press Enter or a comma to add one."
              className="sm:col-span-2"
            >
              <TagInput
                value={keywords}
                onChange={edited(setKeywords)}
                suggestions={suggestions.data?.keywords ?? []}
                normalise={normaliseKeyword}
                itemLabel="keyword"
                placeholder="Add a keyword"
                max={MAX_CAMPAIGN_KEYWORDS}
              />
            </Field>
            <Field
              label="Ad variant (optional)"
              description="To tell ads apart. Part of the link only."
              className="sm:col-span-2"
            >
              <Input value={content} onChange={(event) => edited(setContent)(event.target.value)} />
            </Field>
          </div>
          <div className="mt-5 flex flex-col gap-3 border-t border-border pt-5">
            <p className="text-label text-fg-secondary">Your campaign link</p>
            {!ready ? (
              <p className="text-small text-fg-muted">
                Fill in at least one of source, medium or campaign name.
              </p>
            ) : (
              <>
                {filledIn < 3 && (
                  <Alert tone="info" title="Tip">
                    Links with a source, medium and campaign name give the clearest reports.
                  </Alert>
                )}
                <p className="rounded-md bg-surface-sunken p-3 font-mono text-small break-all text-fg select-all">
                  {saved ?? preview}
                </p>
                {saved ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <CopyButton value={saved} label="Copy campaign link" />
                    <p className="text-small text-fg-secondary">
                      Saved. Leads from this link show under Leads → Campaigns.
                    </p>
                  </div>
                ) : canSave ? (
                  <Button
                    className="w-fit"
                    loading={save.isPending}
                    onClick={() => save.mutate()}
                  >
                    <Link2 /> Save and copy link
                  </Button>
                ) : (
                  <CopyButton value={preview} label="Copy campaign link" />
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
